import { useState, useEffect, useRef, useCallback } from 'react';
import type { FunctionComponent } from 'react';

import Flex from '../../Flex';
import MonthInput from '../Input/MonthInput';
import Icon, { registerIcon } from '../../Icon';
import * as caretLeftIcon from '../../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../../Icon/icons/caret-right.icon';
import { useConfiguration, useDirection, useI18n, useTestIds } from '../../../hooks';
import { getCalendarTypeFromLocale, getSelectedDateInfoText, parseToDate } from '../Input/utils';
import TimeInput from '../Input/TimeInput';
import VisuallyHiddenText from '../../VisuallyHiddenText';
import { getDatePickerTestIds } from '../DateTime.test-ids';
import { withTestIds } from '../../../utils';
import { getAdjustedUTCDateTime, getAdjustedUTCDateTimeForTimezone } from '../utils';
import { CALENDAR_TYPES } from '../Input/local';

import Calendar from './Calendar';
import type DatePickerProps from './DatePicker.types';
import { shiftDateByMonth, isValidDate, toDateTimeString, getCurrentMonthYearLabel } from './utils';
import {
  StyledDatePicker,
  StyledDatePickerMonthInput,
  StyledMonthPicker,
  StyledMonthNavigationButton
} from './DatePicker.styles';

registerIcon(caretLeftIcon, caretRightIcon);

const DatePicker: FunctionComponent<DatePickerProps> = ({
  testId,
  selected: initial,
  calendarType: calendarTypeProp,
  onChange,
  min,
  max,
  mode = 'date',
  showWeekNumber,
  withSeconds,
  pickerInterval,
  clockFormat,
  timeZone,
  ...restProps
}: DatePickerProps) => {
  const getNow = useCallback(
    () => (timeZone ? getAdjustedUTCDateTimeForTimezone(timeZone) : getAdjustedUTCDateTime()),
    [timeZone]
  );
  const testIds = useTestIds(testId, getDatePickerTestIds);

  const { locale } = useConfiguration();
  const t = useI18n();

  const [selected, setSelected] = useState(initial);
  const [current, setCurrent] = useState(() =>
    selected && isValidDate(selected) ? selected : getNow()
  );

  useEffect(() => {
    if (toDateTimeString(initial, mode) !== toDateTimeString(selected, mode)) {
      setSelected(initial);
      setCurrent(initial || getNow());
    }
  }, [toDateTimeString(initial, mode), getNow]);

  const calendarRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    // need to postpone focusing to make sure onFocusWithin has been attached
    setTimeout(() => {
      calendarRef.current?.querySelector<HTMLElement>('button[tabindex="0"]:enabled')?.focus();
    });
  }, []);

  const getTodayButtonLabel = () => {
    if (mode === 'datetime') {
      return t('set_current_date_and_time_a11y');
    }
    if (mode === 'week') {
      return t('set_current_week_ally');
    }
    return t('set_current_date_a11y');
  };
  const todayButtonLabel = getTodayButtonLabel();

  const calendarType =
    calendarTypeProp ??
    (mode === 'week' ? CALENDAR_TYPES.ISO_8601 : getCalendarTypeFromLocale(locale));

  const { start, end } = useDirection();
  return (
    <StyledDatePicker
      data-testid={testIds.root}
      aria-label={t('calendar')}
      role='region'
      {...restProps}
    >
      <Flex as={StyledMonthPicker} container={{ justify: 'between', alignItems: 'end' }}>
        <MonthInput
          testId={testIds.month}
          as={StyledDatePickerMonthInput}
          innerLabelsHidden={false}
          value={current}
          required
          role='group'
          aria-label={t('picker_jump_to_month_and_year')}
          onChange={({ valueAsTimestamp }) => {
            let newCurrent = new Date(valueAsTimestamp!);

            if (max) {
              const maxDate = parseToDate(max);
              if (newCurrent > maxDate) newCurrent = maxDate;
            }

            if (min) {
              const minDate = parseToDate(min);
              if (newCurrent < minDate) newCurrent = minDate;
            }

            setCurrent(newCurrent);
          }}
          min={min}
          max={max}
        />
        <VisuallyHiddenText aria-live='polite' role='alert'>
          {selected === current
            ? getSelectedDateInfoText(current, mode, locale, t)
            : getCurrentMonthYearLabel(current, locale)}
        </VisuallyHiddenText>
        <StyledMonthNavigationButton
          data-testid={testIds.previousMonth}
          label={t('picker_prev_month')}
          variant='simple'
          icon
          onClick={() => {
            const newCurrent = shiftDateByMonth(current, -1);
            setCurrent(newCurrent);
          }}
          disabled={(() => {
            const newCurrent = new Date(current);
            newCurrent.setUTCDate(1);

            return Boolean(min && min > newCurrent);
          })()}
        >
          <Icon name={`caret-${start}`} />
        </StyledMonthNavigationButton>
        <StyledMonthNavigationButton
          data-testid={testIds.setCurrentDate}
          label={todayButtonLabel}
          icon
          variant='simple'
          onClick={() => {
            const currDate = getNow();
            setCurrent(currDate);
            setSelected(currDate);
            onChange?.(currDate);
          }}
        >
          <Icon name='calendar-now' />
        </StyledMonthNavigationButton>
        <StyledMonthNavigationButton
          data-testid={testIds.nextMonth}
          label={t('picker_next_month')}
          icon
          variant='simple'
          onClick={() => {
            const nextCurrent = shiftDateByMonth(current, +1);
            setCurrent(nextCurrent);
          }}
          disabled={(() => {
            const newCurrent = new Date(current);
            newCurrent.setUTCMonth(newCurrent.getUTCMonth() + 1);
            newCurrent.setUTCDate(0);
            return Boolean(max && max < newCurrent);
          })()}
        >
          <Icon name={`caret-${end}`} />
        </StyledMonthNavigationButton>
      </Flex>
      <Flex container={{ wrap: 'nowrap', justify: 'center' }} dir='ltr'>
        <Calendar
          testId={testIds.calendar}
          ref={calendarRef}
          selected={selected && isValidDate(selected) ? selected : undefined}
          current={current}
          calendarType={calendarType}
          showWeekNumber={showWeekNumber}
          mode={mode === 'datetime' ? 'date' : mode}
          onFocus={setCurrent}
          onSelect={selectedDate => {
            const newDate = new Date(selectedDate);

            if (selected) {
              newDate.setUTCHours(selected.getUTCHours());
              newDate.setUTCMinutes(selected.getUTCMinutes());
              newDate.setUTCSeconds(selected.getUTCSeconds());
            }

            setCurrent(newDate);
            setSelected(newDate);
            onChange?.(newDate);
          }}
          min={min}
          max={max}
          timeZone={timeZone}
        />
      </Flex>

      {mode === 'datetime' && (
        <TimeInput
          testId={testIds.time}
          label={t('time_text')}
          value={selected}
          pickerInterval={pickerInterval}
          withSeconds={withSeconds}
          clockFormat={clockFormat}
          onChange={selectedTime => {
            if (selectedTime.state || !selectedTime.valueAsISOString) {
              return;
            }
            const newDate = new Date(selected ?? current);
            const selectedTimeDateObject = new Date(selectedTime.valueAsISOString);
            newDate.setUTCHours(selectedTimeDateObject.getUTCHours());
            newDate.setUTCMinutes(selectedTimeDateObject.getUTCMinutes());
            newDate.setUTCSeconds(selectedTimeDateObject.getUTCSeconds());

            setSelected(newDate);
            onChange?.(newDate);
          }}
          min={min}
          max={max}
        />
      )}
    </StyledDatePicker>
  );
};

export default withTestIds(DatePicker, getDatePickerTestIds);
